<template>
  <button
    :class="[
      baseClasses,
      variantClasses,
      sizeClasses,
      disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
    ]"
    v-bind="$attrs"
    @click="$emit('click', $event)"
  >
    <slot></slot>
  </button>
</template>

<script setup>
import { computed, defineEmits, defineProps } from 'vue';
import { useModeStore } from '../store/mode.js';
import { useThemeStore } from '../store/theme.js';

const props = defineProps({
  variant: {
    type: String,
    default: 'primary',
    validator: (value) => ['primary', 'secondary', 'tertiary', 'quaternary'].includes(value)
  },
  size: {
    type: String,
    default: 'medium',
    validator: (value) => ['small', 'medium', 'large'].includes(value)
  },
  disabled: {
    type: Boolean,
    default: false
  }
});

const modeStore = useModeStore();
const themeStore = useThemeStore();

const baseClasses = computed(() => [
  'font-semibold',
  'rounded-md',
  'transition-colors',
  'duration-200',
  'ease-in-out',
  'focus:outline-none',
  'focus:ring-2',
  'focus:ring-offset-2',
  'focus:ring-orange-500'
]);

const variantClasses = computed(() => {
  const isDark = modeStore.selectedMode === 'dark';

    // Define theme-based background colors for 'primary' variant
  const themeBgColors = {
    green: 'bg-[#14B8A6]',
    orange: 'bg-[#F97316]',
    blue: 'bg-[#3B82F6]',
    purple: 'bg-[#8B5CF6]',
    darkpink: 'bg-[#DB2777]'
  };

  switch (props.variant) {
    case 'primary':
      return [
        themeBgColors[themeStore.selectedTheme] || 'bg-orange-500', // Use theme color or default to orange
        'text-white',
        'hover:bg-opacity-90'
      ];
    case 'secondary':
      return isDark
        ? ['bg-gray-700', 'text-white', 'hover:bg-gray-600']
        : ['bg-gray-200', 'text-gray-800', 'hover:bg-gray-300'];
    case 'tertiary':
      return isDark
        ? ['bg-transparent', 'text-white', 'hover:bg-gray-700']
        : ['bg-transparent', 'text-gray-800', 'hover:bg-gray-100'];
    case 'quaternary':
      return ['bg-white', 'text-black', 'hover:bg-gray-300'];
    default:
      return [];
  }
});

const sizeClasses = computed(() => {
  switch (props.size) {
    case 'small':
      return ['px-3', 'py-1', 'text-sm'];
    case 'medium':
      return ['px-4', 'py-2', 'text-base'];
    case 'large':
      return ['px-6', 'py-3', 'text-lg'];
    default:
      return [];
  }
});

defineEmits(['click']);
</script>